import { Box, BoxProps, ColorOptions, Icon, Text } from '@jfs/ui-new'
import { useNavigation } from '@react-navigation/native'
import { FC, useState } from 'react'
import { FlatList } from 'react-native'
import { ModBox } from '../ModifiedBox/ModifiedBox'
import { SvgImage } from '../SvgImage'
import styles from './styles'
import type { ListItemProps } from './types'
import { ListItemType, ListTypes } from './types'

const listStrings = {
  selected: 'SELECTED',
  original: 'ORIGINAL',
  testMode: 'Test Mode',
}

const DIVIDER = 'DIVIDER'

const RenderSideMenuSeparator = ({ show, data }: any) => {
  const isTextData = data?.value !== undefined
  if (show) {
    return (
      <Box {...styles.DEFAULT.separator(isTextData)}>
        <ModBox {...styles.DEFAULT.textContainer} show={isTextData}>
          <Text {...styles.DEFAULT.sideMenuSubText}>{data && data?.value}</Text>
        </ModBox>
      </Box>
    )
  } else {
    return null
  }
}

const SubMenuItem = ({ data, index, handleSubMenuItemPress, selectedPos }) => {
  const isSelected = selectedPos === index
  const onHandlePress = () => {
    handleSubMenuItemPress(index, data)
  }
  return (
    <Box
      direction="row"
      onPress={onHandlePress}
      underlayColor={'TRANSPARENT'}
      opacity={isSelected ? 1 : 0.8}
      w={'100%'}
      h={'100%'}
      testID="sub-menu-item"
      bg={isSelected ? 'WHITE_SEPERATOR' : undefined}>
      <Box {...styles.SIDE_MENU[isSelected ? 'SELECTED' : 'ORIGINAL'].leftHighlight} />
      <Box pv={12}>
        <Text
          title={`        ${data.value}`}
          color={'WHITE'}
          fontFamily={'jiotype-medium'}
          fontSize={14}
          opacity={isSelected ? 1 : 0.8}
        />
      </Box>
    </Box>
  )
}

export const ListItem: FC<ListItemProps> = ({ type, data, selected, onPress, showHideSubMenu }: ListItemProps) => {
  const subType = selected || data?.isRead ? 'SELECTED' : 'ORIGINAL'
  const hasSubMenu = data?.hasSubMenu ?? false
  const [isHovering, setIsHovering] = useState(false)
  const [showSubMenu, setShowSubMenu] = useState(false)
  const [selectedPos, setSubMenuSelectedPos] = useState(-1)

  const { navigate } = useNavigation()

  const iconId = data?.iconId || ''
  const getHoverStyle = (): BoxProps => {
    let hoverStyle = {}
    if ((type === ListTypes.SEARCH || type === ListTypes.SEARCH_FILTERS) && isHovering) {
      hoverStyle = { backgroundColor: 'BACKGROUND' }
    }
    return hoverStyle
  }
  const handleHover = (hovering: boolean): void => {
    if (type === ListTypes.SEARCH || type === ListTypes.SEARCH_FILTERS) {
      setIsHovering(hovering)
    }
  }

  const modeColor = () => {
    return data?.value === listStrings.testMode ? 'GREEN' : 'ORANGE'
  }

  const getModesView = () => {
    return <Box margin={0} padding={0} {...[styles[type][subType].leftHighlight, { backgroundColor: modeColor() }]} />
  }

  const getNotificationView = () => {
    return (
      <>
        <Box margin={0} padding={0} {...styles[type][subType].container}>
          <Text {...styles[type][subType].titleStyle}>{data && data?.title}</Text>
          <Text {...styles[type].dateStyle}>{data && data?.date}</Text>
        </Box>
        <Text {...styles[type][subType].itemStyle}>{data && data?.message}</Text>
      </>
    )
  }

  const isDataAvailable = () => {
    return data?.filter?.length > 0 ?? false
  }

  const isDivider = data?.id === DIVIDER

  const handleSubMenu = () => {
    if (hasSubMenu) {
      setSubMenuSelectedPos(0)
      setShowSubMenu((prev) => {
        const showHide = !prev
        showHideSubMenu && showHideSubMenu(showHide, data.id)
        return showHide
      })
    } else {
      setShowSubMenu(false)
      setSubMenuSelectedPos(-1)
      showHideSubMenu(false, '15')
    }
  }

  const handOnPressOfItem = () => {
    setShowSubMenu(false)
    setSubMenuSelectedPos(-1)
    handleSubMenu()
    onPress && onPress()
  }

  const handleSubMenuItemPress = (index: number, subItemdata: any) => {
    setSubMenuSelectedPos(index)
    if (subItemdata && subItemdata?.navigateTo) {
      navigate(subItemdata?.navigateTo)
    }
  }

  return (
    <ModBox
      onPress={handOnPressOfItem}
      onHover={handleHover}
      underlayColor={'TRANSPARENT'}
      key={data.id}
      testID={`menu-item-${data.id}`}>
      <>
        <ModBox show={!isDivider}>
          <Box {...styles[type][subType].container} {...getHoverStyle()}>
            {type === ListTypes.MODES && getModesView()}
            <Box {...styles[type][subType].leftHighlight} />
            {(iconId.length > 0 || type === ListTypes.NOTIFICATION) && (
              <Box>
                {!data?.svgImageName && (
                  <Icon id={data?.iconId || 'USER'} color={styles[type].iconColor as ColorOptions} size={16} />
                )}
                {data?.svgImageName?.length > 0 && (
                  <SvgImage imageName={data.svgImageName} width={16} height={16} color={styles[type].iconColor} />
                )}
              </Box>
            )}

            {isDataAvailable() && (
              <Box {...styles[type][subType].filterOuterContainer}>
                <Box {...styles[type][subType].filterContainer}>
                  <Text
                    type={'caption'}
                    fontWeight={'MEDIUM'}
                    color={type === ListTypes.SEARCH_FILTERS ? 'BLUE' : 'PRIMARY_GREY'}>
                    {data?.filter}
                  </Text>
                </Box>
              </Box>
            )}
            <Box {...styles[type][subType].itemContainer} marginLeft={iconId.length === 0 ? 16 : 0}>
              {type === ListTypes.NOTIFICATION && getNotificationView()}
              <Text testID={`menu-item-str-${data?.id}`} {...styles[type][subType].itemStyle}>
                {data && data?.value}
              </Text>
            </Box>
            <ModBox
              show={data?.showBetaTag ?? false}
              p={1}
              borderWidth={1}
              borderRadius={2}
              marginHorizontal={6}
              borderColor={'BRAND_PRIMARY_ORANGE'}>
              <Text
                color={styles[type][subType].itemStyle.color}
                title={'BETA'}
                fontFamily="jiotype-bold"
                fontSize={10}
              />
            </ModBox>
            <Box {...styles[type][subType].separatorStyle} />
            {hasSubMenu && (
              <Box marginLeft={32} underlayColor={'TRANSPARENT'}>
                <Icon
                  id={selected ? 'ARROW_UP' : 'ARROW_DOWN'}
                  color={styles[type].iconColor as ColorOptions}
                  size={14}
                />
              </Box>
            )}
          </Box>
        </ModBox>
      </>
      {hasSubMenu && selected && showSubMenu && data?.subMenu?.length > 0 && (
        <FlatList
          data={data?.subMenu}
          renderItem={({ item, index }) => (
            <SubMenuItem
              data={item}
              index={index}
              selectedPos={selectedPos}
              handleSubMenuItemPress={handleSubMenuItemPress}
            />
          )}
        />
      )}
      <RenderSideMenuSeparator show={isDivider} data={data} />
    </ModBox>
  )
}

export { ListItemType, ListTypes }
export type { ListItemProps }

